import React, { useCallback, useReducer, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Button } from '@packages/ui/primitives';
import type { FormActionButtonNode } from '../../types/layout';
import { ActionRegistry } from '@packages/table-builder/registry';
import { useEventScope } from '@packages/block-events/react/useEventScope';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { evaluateCondition, type ConditionContext } from '../../evaluators/conditionEvaluator';
import { extractConditionFieldPaths } from '../../utils/extractConditionFieldPaths';
import { extractFilesFromFormValue } from '../../utils/filePayload';

interface FormActionButtonRendererProps {
  node: FormActionButtonNode;
}

interface ActionButtonState {
  remainingSeconds: number;
  isLoading: boolean;
}

type ActionButtonAction =
  | { type: 'START_COOLDOWN'; seconds: number }
  | { type: 'TICK' }
  | { type: 'RESET_COOLDOWN' }
  | { type: 'LOADING_START' }
  | { type: 'LOADING_END' };

function actionButtonReducer(state: ActionButtonState, action: ActionButtonAction): ActionButtonState {
  switch (action.type) {
    case 'START_COOLDOWN':
      return { ...state, remainingSeconds: action.seconds };
    case 'TICK':
      return state.remainingSeconds <= 1
        ? { ...state, remainingSeconds: 0 }
        : { ...state, remainingSeconds: state.remainingSeconds - 1 };
    case 'RESET_COOLDOWN':
      return { ...state, remainingSeconds: 0 };
    case 'LOADING_START':
      return { ...state, isLoading: true };
    case 'LOADING_END':
      return { ...state, isLoading: false };
  }
}

/** String params for ActionRegistry; staged File uploads stay on `ctx.data` (not JSON-serializable). */
export function serializeFormValuesForActionParams(
  values: Record<string, unknown>,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value == null) continue;
    if (extractFilesFromFormValue(value).length > 0) continue;
    if (typeof value === 'string') {
      params[key] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      params[key] = String(value);
    } else {
      params[key] = JSON.stringify(value);
    }
  }
  return params;
}

/** Deep-clone form values for action `data` — avoids passing live RHF references into actions. */
function clonePlainFormValues(values: Record<string, unknown>): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(values)) as Record<string, unknown>;
  } catch {
    return { ...values };
  }
}

function applyActionResultToForm(
  form: ReturnType<typeof useFormEngineForm>['form'],
  result: Record<string, unknown>,
): void {
  const clearFields = result.clearFields;
  if (!Array.isArray(clearFields)) return;
  for (const field of clearFields) {
    if (typeof field !== 'string' || !field.trim()) continue;
    form.setValue(field, null, { shouldDirty: true, shouldValidate: true });
  }
}

export const FormActionButtonRenderer = React.memo(function FormActionButtonRenderer({
  node,
}: FormActionButtonRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { params: blockParams, formSessionKey, isReadOnly, meta, getOptionProp } =
    useFormEngineState();
  const eventScope = useEventScope();
  const [state, dispatch] = useReducer(actionButtonReducer, { remainingSeconds: 0, isLoading: false });

  const conditionWatchPaths = useMemo(() => {
    const paths = new Set<string>();
    if (node.hiddenWhen) {
      for (const p of extractConditionFieldPaths(node.hiddenWhen)) paths.add(p);
    }
    if (node.disabledWhen) {
      for (const p of extractConditionFieldPaths(node.disabledWhen)) paths.add(p);
    }
    return Array.from(paths);
  }, [node.hiddenWhen, node.disabledWhen]);

  useWatch({ control: form.control, name: conditionWatchPaths, disabled: conditionWatchPaths.length === 0 });

  React.useEffect(() => {
    if (state.remainingSeconds <= 0) return;
    const timer = setInterval(() => dispatch({ type: 'TICK' }), 1000);
    return () => clearInterval(timer);
  }, [state.remainingSeconds]);

  const handleClick = useCallback(async () => {
    if (state.remainingSeconds > 0 || state.isLoading) return;

    const values = form.getValues() as Record<string, unknown>;

    if (node.externalRoute) {
      ActionRegistry.getNavigate()?.(node.externalRoute);
    }

    dispatch({ type: 'LOADING_START' });
    try {
      const result = await ActionRegistry.executeData(
        node.actionId,
        {
          ...blockParams,
          ...serializeFormValuesForActionParams(values),
          ...(formSessionKey ? { formSessionKey } : {}),
        },
        node.passFormData ? clonePlainFormValues(values) : values,
        eventScope ? { eventScope, mode: meta.mode as 'create' | 'edit' | 'view' | undefined } : undefined,
      );
      applyActionResultToForm(form, result);
    } finally {
      dispatch({ type: 'LOADING_END' });
    }

    if (node.cooldownSeconds && node.cooldownSeconds > 0) {
      dispatch({ type: 'START_COOLDOWN', seconds: node.cooldownSeconds });
    }
  }, [
    state.remainingSeconds,
    state.isLoading,
    form,
    node.actionId,
    node.cooldownSeconds,
    node.externalRoute,
    node.passFormData,
    blockParams,
    formSessionKey,
    eventScope,
    meta.mode,
  ]);

  const buttonLabel = useMemo(() => {
    const modeLabel = node.labelByMode?.[meta.mode as string];
    const baseLabel = modeLabel ?? node.label;
    if (state.remainingSeconds > 0) {
      return `${baseLabel} (${state.remainingSeconds}s)`;
    }
    return baseLabel;
  }, [state.remainingSeconds, node.label, node.labelByMode, meta.mode]);

  const formValues = form.getValues() as Record<string, unknown>;
  if (node.hiddenWhen) {
    const condCtx: ConditionContext = { fieldValue: undefined, values: formValues, meta, getOptionProp };
    if (evaluateCondition(node.hiddenWhen, condCtx)) return null;
  }

  // Hide action buttons in read-only/view mode unless explicitly opted in
  if (isReadOnly && !node.visibleInViewMode) return null;

  let conditionDisabled = false;
  if (node.disabledWhen) {
    const condCtx: ConditionContext = { fieldValue: undefined, values: formValues, meta, getOptionProp };
    conditionDisabled = evaluateCondition(node.disabledWhen, condCtx);
  }

  const isDisabled = state.remainingSeconds > 0 || conditionDisabled;

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: node.align === 'start' ? 'flex-start' : 'flex-end',
      }}
    >
      <Button
        variant={(node.variant as any) ?? 'primary-outline'}
        icon={node.icon}
        label={buttonLabel}
        onClick={handleClick}
        disabled={isDisabled}
        loading={state.isLoading}
        type='button'
      />
    </div>
  );
});
